import { Navigate, Outlet } from 'react-router-dom';
import { useEffect, useEffectEvent, useMemo, useState } from 'react';

import { FullScreenBrandLoader } from '@/components/shared/full-screen-brand-loader';
import { checkMFARequirementStrict } from '@/lib/auth-utils';
import { sessionAccessKey } from '@/contexts/auth-session-transitions';

import { useAuth } from '../../contexts/auth-context';
import { useOrganization } from '../../contexts/organization-context';

interface ProtectedRouteProps {
  readonly redirectTo?: string;
}

interface AccessCheckInputs {
  readonly userId: string | undefined;
  readonly sessionKey: string | null;
  readonly loading: boolean;
  readonly isLoadingOrganizations: boolean;
  readonly hasActiveOrganizations: boolean;
  readonly isPlatformAdmin: boolean;
}

interface AccessDecision {
  readonly inputs: AccessCheckInputs;
  readonly hasAccess: boolean;
  readonly requiresMFA: boolean;
}

interface AccessCheckActions {
  readonly isCurrent: () => boolean;
  readonly finish: (access: boolean, mfaRequired?: boolean) => void;
}

async function checkRouteAccess(
  inputs: AccessCheckInputs,
  actions: AccessCheckActions,
): Promise<void> {
  if (inputs.loading) {
    return;
  }
  if (!inputs.userId) {
    actions.finish(false);
    return;
  }
  if (inputs.isLoadingOrganizations) {
    return;
  }
  if (!inputs.isPlatformAdmin && !inputs.hasActiveOrganizations) {
    actions.finish(false);
    return;
  }

  const mfaCheck = await checkMFARequirementStrict();
  if (!actions.isCurrent()) {
    return;
  }
  if (mfaCheck.status === 'required') {
    actions.finish(true, true);
    return;
  }
  actions.finish(mfaCheck.status !== 'indeterminate');
}

const ProtectedRoute = ({ redirectTo = '/auth/login' }: ProtectedRouteProps) => {
  const { user, session, loading } = useAuth();
  const { userOrganizations, isLoadingOrganizations, isPlatformAdmin } = useOrganization();
  const [accessDecision, setAccessDecision] = useState<AccessDecision | null>(null);

  // Derive a primitive boolean so a TanStack Query refetch returning a fresh
  // userOrganizations array reference doesn't refire the access check below.
  const hasActiveOrganizations = useMemo(
    () => userOrganizations.some((om) => om.status === 'active'),
    [userOrganizations],
  );
  const userId = user?.id;
  const sessionKey = sessionAccessKey(session);
  const accessCheckInputs = useMemo<AccessCheckInputs>(
    () => ({
      userId,
      sessionKey,
      loading,
      isLoadingOrganizations,
      hasActiveOrganizations,
      isPlatformAdmin,
    }),
    [userId, sessionKey, loading, isLoadingOrganizations, hasActiveOrganizations, isPlatformAdmin],
  );
  const isCurrentAccessCheck = useEffectEvent(
    (inputs: AccessCheckInputs) => inputs === accessCheckInputs,
  );

  useEffect(() => {
    let canceled = false;
    const inputs = accessCheckInputs;
    const isExactCurrentCheck = () => !canceled && isCurrentAccessCheck(inputs);

    const finishCheck = (access: boolean, mfaRequired = false) => {
      if (!isExactCurrentCheck()) {
        return;
      }
      setAccessDecision({ inputs, hasAccess: access, requiresMFA: mfaRequired });
    };

    const handleCheckFailure = (error: unknown) => {
      if (!isExactCurrentCheck()) {
        return;
      }
      console.error('Error checking protected route access:', error);
      finishCheck(false);
    };

    checkRouteAccess(inputs, {
      isCurrent: isExactCurrentCheck,
      finish: finishCheck,
    }).catch(handleCheckFailure);

    return () => {
      canceled = true;
    };
  }, [accessCheckInputs]);

  const hasCurrentDecision = accessDecision?.inputs === accessCheckInputs;

  if (loading || isLoadingOrganizations || !hasCurrentDecision) {
    return <FullScreenBrandLoader />;
  }

  // Redirect if no user or no access
  if (!user || !accessDecision.hasAccess) {
    return <Navigate replace to={redirectTo} />;
  }

  if (accessDecision.requiresMFA) {
    return <Navigate replace to="/auth/mfa-verify" />;
  }

  return <Outlet />;
};

export default ProtectedRoute;
